import React, { useState } from "react";
import HashIcon from "assets/icons/Hash.svg";
import BookMark from "assets/icons/BookMark.svg";
import BriefCase from "assets/icons/BriefCase.svg";
import LocationIcon from "assets/icons/Location.svg";
import CalendarIcon from "assets/icons/Calendar.svg";
import BookMarkSaved from "assets/icons/BookMarkSaved.svg";
import { useNavigate } from "react-router-dom";
import GenericModal from "../../Ui/Modal";
import PlusIcon from "assets/icons/Plus.svg";
import CustomPagination from "./Pagination";
import { status, statusColors, statusDots } from "../../constant/Home/Common";
import { useContext } from "react";
import { UserContext } from "../../context/UserContext";
import ButtonComponent from "../../Ui/Button";
import { useCurrentSelectedData } from "../../context/CurrentSelectedDataContext";
import { formatDate, isWarrantyExpired } from "../../utils/utils";
import toast from "react-hot-toast";

const warrantyStatusColors = {
  SUBMITTED: "bg-[#FFF7E8] text-[#B86E00] border-[#FFD699]",
  APPROVED: "bg-[#F2FBF4] text-[#2F8F48] border-[#B9E7C7]",
  DENIED: "bg-[#FFF1F1] text-[#C62828] border-[#F4BBBB]",
  RESOLVED: "bg-[#F3F6FB] text-[#49627A] border-[#D9E3EF]",
};

// Make enum-like strings human readable: PROPERTY_GROUNDS -> Property Grounds
const formatIssueType = (value) => {
  if (!value || typeof value !== "string") return "";
  return value
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const ReusableCard = ({
  resultsPerPage,
  jobs,
  classGrid,
  showPagination = true,
  setModalState,
  title = "",
  onManageJobClick,
  onScheduleWarrantyClick,
  onViewWarrantyDetailsClick,
  currentPage,
  setCurrentPage,
  totalCount,
  onResultsPerPageChange,
}) => {
  const { userData } = useContext(UserContext);
  const { setCurrentSelectedData } = useCurrentSelectedData();
  const [viewingInvoiceId, setViewingInvoiceId] = useState(null);
  const [showWarrantyExpired, setShowWarrantyExpired] = useState(false);
  const navigate = useNavigate();

  const handleWarrantyClick = (job) => {
    if (isWarrantyExpired(job)) {
      setShowWarrantyExpired(true);
      return;
    }
    const jobId = job?.id || job?.jobNumber || job?.jobId;
    navigate(`/warranty-service/${jobId}`.replace(/\/$/, ""), {
      state: { invoice: { jobSnapshot: job, ...job } },
    });
  };

  const handleViewInvoice = (job) => {
    const id = job?.id;
    if (!id) return toast.error("Missing job id");

    // Vendor jobs now include this directly from /api/maintenance-requests/
    const url = job?.stripe_invoice_url || job?.stripeInvoiceUrl || null;

    if (!url) {
      toast.error("Invoice/receipt link isn't available yet.");
      return;
    }

    setViewingInvoiceId(id);
    try {
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.log("Error opening invoice URL:", e);
      toast.error("Couldn't open invoice link.");
    } finally {
      setViewingInvoiceId(null);
    }
  };

  return (
    <div className="w-full flex flex-col md:gap-8 gap-4 ">
      <div className={`grid ${classGrid} `}>
        {jobs.map((job) => {
          const normalizedStatus = String(job?.status || "").trim().toUpperCase();
          const isVendor = userData.accountType === "VENDOR";
          const isTerminalStatus =
            normalizedStatus === "COMPLETED" ||
            normalizedStatus === "CANCELED" ||
            normalizedStatus === "CANCELLED";
          const activeWarrantyClaim =
            job?.activeWarrantyClaim || job?.active_warranty_claim || null;
          const hasApprovedWarrantyClaim =
            String(activeWarrantyClaim?.status || "").toUpperCase() === "APPROVED";
          const warrantyStatusKey = String(activeWarrantyClaim?.status || "").toUpperCase();
          const warrantyStatusLabel = activeWarrantyClaim?.statusDisplay || activeWarrantyClaim?.status_display;
          const statusKey = normalizedStatus || job?.status;
          const canScheduleWarrantyRepair =
            Boolean(onScheduleWarrantyClick) &&
            normalizedStatus === "COMPLETED" &&
            hasApprovedWarrantyClaim;
          const canViewWarrantyDetails =
            Boolean(onViewWarrantyDetailsClick) && Boolean(activeWarrantyClaim?.id);
          const displayStatusLabel = activeWarrantyClaim
            ? `Warranty ${warrantyStatusLabel || warrantyStatusKey}`
            : status[statusKey] || status[job.status] || String(job?.status || "");
          const displayStatusClasses = activeWarrantyClaim
            ? warrantyStatusColors[warrantyStatusKey] || "bg-[#F6F6F6] text-[#595959] border-[#ECECEC]"
            : statusColors[statusKey] || "";

          const invoiceUrl = job?.stripe_invoice_url || job?.stripeInvoiceUrl || null;

          return (
            <div key={job.id} className="bg-white flex justify-between gap-[10px] flex-wrap  rounded-[20px] border border-[#ECECEC] md:p-5 p-3">
              <div className="flex flex-col items-start  gap-3">
                <h2 className="md:text-[18px] text-[14px] font-medium text-[#242424]">
                  {formatIssueType(job?.issueType)}
                </h2>
                <div className="flex items-start md:items-center flex-col md:flex-row flex-wrap text-sm md:gap-6 gap-3">
                  <div className="flex items-center gap-2">
                    <img src={HashIcon} />
                    <span className="text-[#595959] font-medium text-[14px]">
                      {job?.jobNumber}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <img src={LocationIcon} />
                    <span className="text-[#595959] font-medium text-[14px]">
                      {job?.propertyAddress?.streetAddress}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <img src={CalendarIcon} />
                    <span className="text-[#595959] font-medium text-[14px]">
                      {formatDate(job?.createdAt)}
                    </span>
                  </div>
                  {!!job.tasks && (
                    <div className="flex items-center gap-2">
                      <img src={BriefCase} />
                      <span className="text-[#595959] font-medium text-[14px]">
                        {job.tasks}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {!isVendor ? (
                <div className="flex items-center flex-col  gap-2">
                  <span
                    className={`text-[12px] font-medium px-3 py-1 border rounded-full flex items-center gap-2 ${displayStatusClasses}`}
                  >
                    {displayStatusLabel}
                  </span>
                  {title === "Active Jobs" && (
                    <button
                      className="border border-[#ECECEC] px-4 py-2 rounded-[12px] bg-transparent text-[14px]"
                      onClick={() => {
                        setCurrentSelectedData(job);
                        onManageJobClick();
                      }}
                    >
                      Manage
                    </button>
                  )}
                  {title === "Past Jobs" && normalizedStatus === "COMPLETED" && !activeWarrantyClaim && (
                    <button
                      className="border border-[#ECECEC] px-4 py-2 rounded-[12px] bg-transparent text-[14px]"
                      onClick={() => handleWarrantyClick(job)}
                    >
                      Warranty Service
                    </button>
                  )}
                </div>
              ) : isTerminalStatus ? (
                <div className="flex items-end flex-col gap-2">
                  <span
                    className={`text-[12px] font-medium px-3 py-1 border rounded-full flex items-center gap-2 ${displayStatusClasses}`}
                  >
                    {displayStatusLabel}
                  </span>

                  {canViewWarrantyDetails && (
                    <ButtonComponent
                      onClick={() => onViewWarrantyDetailsClick(job)}
                      bgColor="#FFFFFF"
                      color="#242424"
                      borderColor="#ECECEC"
                      maxHeight={{ md: "59px", xs: "49px" }}
                    >
                      View Details
                    </ButtonComponent>
                  )}

                  {canScheduleWarrantyRepair && (
                    <ButtonComponent
                      onClick={() => onScheduleWarrantyClick(job)}
                      bgColor="#009FD4"
                      color="#FFFFFF"
                      borderColor="#009FD4"
                      maxHeight={{ md: "59px", xs: "49px" }}
                    >
                      Schedule Warranty Repair
                    </ButtonComponent>
                  )}

                  {!!invoiceUrl && (
                    <ButtonComponent
                      href={invoiceUrl || undefined} target="_blank" rel="noopener noreferrer" component="a"
                      bgColor="#FFFFFF"
                      color="#242424"
                      borderColor="#ECECEC"
                      maxHeight={{ md: "59px", xs: "49px" }}
                      disabled={viewingInvoiceId === job?.id}
                    >
                      View Invoice
                    </ButtonComponent>
                  )}
                </div>
              ) : (
                <ButtonComponent
                  onClick={() => {
                    setCurrentSelectedData(job);
                    onManageJobClick();
                  }}
                  bgColor="#52C2711A"
                  color="#52C271"
                  borderColor="#52C271"
                  maxHeight={{ md: "59px", xs: "49px" }}
                >
                  Manage Job
                </ButtonComponent>
              )}
            </div>
          );
        })}
      </div>
      {showPagination && (
        <CustomPagination
          totalCount={totalCount}
          resultsPerPage={resultsPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          onResultsPerPageChange={onResultsPerPageChange}
          padding={false}
        />
      )}
      {/* Warranty Expired Modal */}
      <GenericModal
        open={showWarrantyExpired}
        onClose={() => setShowWarrantyExpired(false)}
        title=" "
        logoDetail="Warranty Expired"
        children={
          <div className="md:p-6 p-4">
            <p className="text-center text-[#595959] text-[12px] max-w-[720px] m-auto">
              The warranty for this job has expired. If you are experiencing issues, please submit a new job request
              and we will help get your problem resolved.
            </p>
          </div>
        }
        footer={
          <div className="w-full flex justify-end">
            <ButtonComponent
              bgColor="#3CAB5C"
              color="#FFFFFF"
              onClick={() => {
                setShowWarrantyExpired(false);
                navigate("/post-job");
              }}
            >
              <span className="flex items-center gap-2">
                <img src={PlusIcon} alt="plus" className="w-[14px] h-[14px]" />
                Post a job
              </span>
            </ButtonComponent>
          </div>
        }
        maxWidth="756px"
      />
    </div>
  );
};

export default ReusableCard;
